import React, {useState} from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Card,
  CardContent,
  Chip,
  Alert,
  Checkbox,
  FormControlLabel,
} from '@mui/material';
import {useSaveSummary} from '../../../hooks/productions/useProductions';
import {SaveSummaryRequestBody} from '../../../api/types/production.type';
import '../Demo.scss';

const SaveSummaryApiTest: React.FC = () => {
  const [productionId, setProductionId] = useState<number>(59);
  const [securityGroupId, setSecurityGroupId] = useState<number>(1);
  const [isMarkingComplete, setIsMarkingComplete] = useState<boolean>(true);
  const [saveResult, setSaveResult] = useState<string>('');
  const [copySuccess, setCopySuccess] = useState<boolean>(false);

  const saveMutation = useSaveSummary(
    (data) => {
      setSaveResult(`✅ ${data.message}`);
    },
    (error) => {
      setSaveResult(`❌ Save failed: ${error.message}`);
    }
  );

  const handleSave = () => {
    const requestBody: SaveSummaryRequestBody = {
      productionId: productionId,
      securityGroupId: securityGroupId,
      isMarkingComplete: isMarkingComplete,
    };
    saveMutation.mutate(requestBody);
  };

  const handleReset = () => {
    setSaveResult('');
  };

  const handleCopyJson = async () => {
    if (saveMutation.data) {
      try {
        await navigator.clipboard.writeText(
          JSON.stringify(saveMutation.data, null, 2)
        );
        setCopySuccess(true);
        setTimeout(() => setCopySuccess(false), 2000);
      } catch (err) {
        console.error('Failed to copy:', err);
      }
    }
  };

  return (
    <Box className="demo-page" sx={{padding: 3}}>
      <Box sx={{marginBottom: 4}}>
        <Typography
          variant="h4"
          className="typo-text headline1_size"
          gutterBottom
        >
          🧪 Save Summary API Test
        </Typography>
        <Typography className="typo-text body2_size" sx={{color: '#666'}}>
          Mark production as complete. Updates the production status to mark it
          as complete.
        </Typography>
      </Box>
      {/* API Info */}
      <Card sx={{marginBottom: 3}}>
        <CardContent>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              marginBottom: 2,
            }}
          >
            <Chip label="POST" color="success" size="small" data-testid={`save-summary-chip-info`} />
            <Typography variant="h6" className="typo-text subhead2_size">
              Save Summary (Mark Complete)
            </Typography>
          </Box>
          <Typography
            className="typo-text body3_size"
            sx={{
              fontFamily: 'monospace',
              backgroundColor: '#f5f5f5',
              padding: '4px 8px',
              borderRadius: '4px',
              display: 'inline-block',
              marginBottom: 1,
              fontSize: '12px',
            }}
          >
            /productions/api/v1/production/summary
          </Typography>
          <Typography className="typo-text body3_size" sx={{color: '#666'}}>
            Mark production as complete. POST request with request body
            (productionId, securityGroupId, isMarkingComplete). Returns a
            success message.
          </Typography>
        </CardContent>
      </Card>
      {/* Request Body */}
      <Card sx={{marginBottom: 3}}>
        <CardContent>
          <Typography
            variant="h6"
            className="typo-text subhead3_size"
            gutterBottom
          >
            Request Body
          </Typography>

          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
              gap: 2,
              marginBottom: 2,
            }}
          >
            <TextField
              label="Production ID"
              type="number"
              value={productionId}
              onChange={(e) => setProductionId(Number(e.target.value))}
              size="small"
            />
            <TextField
              label="Security Group ID"
              type="number"
              value={securityGroupId}
              onChange={(e) => setSecurityGroupId(Number(e.target.value))}
              size="small"
            />
            <FormControlLabel
              control={
                <Checkbox
                  data-testid="save-summary-api-test-is-marking-complete"
                  checked={isMarkingComplete}
                  onChange={(e) => setIsMarkingComplete(e.target.checked)}
                />
              }
              label="Is Marking Complete"
            />
          </Box>

          <Box sx={{display: 'flex', gap: 2}}>
            <Button
              variant="contained"
              color="primary"
              onClick={handleSave}
              disabled={saveMutation.isPending}
              data-testid="save-summary-button"
            >
              {saveMutation.isPending ? '⏳ Saving...' : '💾 Mark Complete'}
            </Button>
            <Button
              variant="outlined"
              onClick={handleReset}
              data-testid="save-summary-api-test-reset-button">
              🔄 Reset
            </Button>
            {saveMutation.data && (
              <Button
                variant="outlined"
                onClick={handleCopyJson}
                data-testid="save-summary-copy-button"
              >
                {copySuccess ? '✅ Copied!' : '📋 Copy JSON'}
              </Button>
            )}
          </Box>
        </CardContent>
      </Card>
      {/* Loading State */}
      {saveMutation.isPending && (
        <Card sx={{marginBottom: 3}}>
          <CardContent>
            <Alert severity="info">
              ⏳ Marking production as complete... Please wait...
            </Alert>
          </CardContent>
        </Card>
      )}
      {/* Error State */}
      {saveMutation.isError && (
        <Card sx={{marginBottom: 3}}>
          <CardContent>
            <Alert severity="error">
              <Typography className="typo-text subhead3_size" gutterBottom>
                ❌ Error
              </Typography>
              <Typography className="typo-text body3_size">
                {saveMutation.error?.message ||
                  'An error occurred while saving summary'}
              </Typography>
            </Alert>
          </CardContent>
        </Card>
      )}
      {/* Success State */}
      {saveMutation.isSuccess && saveMutation.data && (
        <>
          <Card sx={{marginBottom: 3}}>
            <CardContent>
              <Typography
                variant="h6"
                className="typo-text subhead3_size"
                gutterBottom
              >
                Save Result
              </Typography>

              <Alert severity="success" sx={{marginBottom: 2}}>
                <Typography className="typo-text subhead3_size" gutterBottom>
                  ✅ Success
                </Typography>
                <Typography className="typo-text body3_size">
                  {saveMutation.data.message}
                </Typography>
              </Alert>
            </CardContent>
          </Card>

          {/* JSON Response */}
          <Card>
            <CardContent>
              <Typography
                variant="h6"
                className="typo-text subhead3_size"
                gutterBottom
              >
                JSON Response
              </Typography>
              <Box
                sx={{
                  backgroundColor: '#f5f5f5',
                  padding: 2,
                  borderRadius: 1,
                  fontFamily: 'monospace',
                  fontSize: '12px',
                  maxHeight: 300,
                  overflow: 'auto',
                }}
              >
                <pre>{JSON.stringify(saveMutation.data, null, 2)}</pre>
              </Box>
            </CardContent>
          </Card>
        </>
      )}
      {/* Result Display */}
      {saveResult && !saveMutation.isPending && (
        <Card>
          <CardContent>
            <Typography
              variant="h6"
              className="typo-text subhead3_size"
              gutterBottom
            >
              Save Result
            </Typography>
            <Alert severity={saveResult.includes('✅') ? 'success' : 'error'}>
              {saveResult}
            </Alert>
          </CardContent>
        </Card>
      )}
    </Box>
  );
};

export default SaveSummaryApiTest;
